import Sortable from "sortablejs";
import { onBeforeUnmount, type ShallowRef, watch } from "vue";

/**
 * `cancelSort` is the globally mounted plugin from the Alpine prompt-config
 * editor (`promptConfigData.js`): SortableJS merges a mounted plugin's defaults
 * into *every* instance in the app, and that plugin's drop handler reverts
 * through the drag element without checking it still exists — so it throws from
 * `destroy()`, which Vue calls while unmounting, aborting the patch and leaving
 * the page frozen mid-render. This list does its own reverting, so opt out.
 */
type ListOptions = Sortable.Options & { cancelSort?: boolean };

export interface SortableMove {
    /** `data-list-id` of the list the item was dragged out of. */
    fromListId: string;
    /** `data-list-id` of the list the item was dropped into. */
    toListId: string;
    oldIndex: number;
    newIndex: number;
}

/**
 * Drag-to-reorder for a list Vue renders, including drags between lists that
 * share a `group`.
 *
 * Sortable mutates the DOM directly, which would fight Vue's rendering, so the
 * drop is undone in the DOM and reported as a `SortableMove` for the caller to
 * apply to its own state — Vue then re-renders the list from the new order.
 */
export function useSortableList(element: Readonly<ShallowRef<HTMLElement | null>>, options: { group: string; handle?: string; onMove: (move: SortableMove) => void }): void {
    let sortable: Sortable | null = null;

    const destroy = () => {
        sortable?.destroy();
        sortable = null;
    };

    watch(
        element,
        (el) => {
            destroy();
            if (!el) {
                return;
            }
            const listOptions: ListOptions = {
                group: options.group,
                handle: options.handle,
                animation: 150,
                cancelSort: false,
                revertOnSpill: false,
                onEnd: (event) => {
                    const { oldIndex, newIndex, from, to, item } = event;
                    if (oldIndex == null || newIndex == null) {
                        return;
                    }
                    from.insertBefore(item, from.children[oldIndex] ?? null);
                    options.onMove({
                        fromListId: from.dataset.listId ?? "",
                        toListId: to.dataset.listId ?? "",
                        oldIndex,
                        newIndex,
                    });
                },
            };
            sortable = Sortable.create(el, listOptions);
        },
        { immediate: true },
    );

    onBeforeUnmount(destroy);
}
